/* =============================================================
   VisaQuiz — United Dream Mongolia
   Conversational quiz that opens when a user clicks a country
   button in the Hero section.
   Flow:
     1. Purpose (travel / study / work)
     2. Age
     3. Employment
     4. Income
     5. Travel history
     6. Prior visa
     7. Refusal history
     8. Family in target country (non-czech)
     9. [Study only] English level
    10. [Czech only] Criminal record / Schengen ban
   Result: probability (own vs United Dream), personalized
           document list, cost summary, "Виз мэдүүлэх" CTA
   ============================================================= */
import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, ChevronLeft, CheckCircle2, ArrowRight } from "lucide-react";
import { useOrderModal } from "@/contexts/OrderModalContext";
import { VISA_PRICING, calcTier, getCurrentPrice } from "@/hooks/useUrgencyPricing";
import { trpc } from "@/lib/trpc";
import { useConsultation } from "@/contexts/ConsultationModalContext";



/* ── Country → visa key mapping ──────────────────────────── */
const COUNTRY_VISA_KEY: Record<string, Record<string, string>> = {
  usa:       { travel: "usa-travel",       study: "usa-study" },
  australia: { travel: "australia-travel", study: "australia-study" },
  canada:    { travel: "canada-travel",    study: "canada-travel" },
  schengen:  { travel: "schengen-travel" },
  ireland:   { study: "ireland-study",     travel: "ireland-study" },
  czech:     { work: "czech-work" },
};

/* ── Purpose options per country ─────────────────────────── */
const COUNTRY_PURPOSES: Record<string, { value: string; emoji: string; label: string; desc: string }[]> = {
  usa:       [
    { value: "travel", emoji: "✈️", label: "Аялалын виз (B1/B2)", desc: "Жуулчлал, гэр бүлийн айлчлал, 10 жил" },
    { value: "study",  emoji: "🎓", label: "Оюутны виз (F-1)",    desc: "Их сургууль, хэлний курс" },
  ],
  australia: [
    { value: "travel", emoji: "✈️", label: "Аялалын виз (600)",    desc: "Жуулчлал, гэр бүлийн айлчлал" },
    { value: "study",  emoji: "🎓", label: "Оюутны виз (500)",     desc: "Их сургууль, мэргэжлийн сургалт" },
  ],
  canada:    [
    { value: "travel", emoji: "✈️", label: "Аялалын виз (TRV)",    desc: "Жуулчлал, 10 жил хүртэл" },
  ],
  schengen:  [
    { value: "travel", emoji: "✈️", label: "Шенгений аялалын виз", desc: "26 улс, 90 хоног хүртэл" },
  ],
  ireland:   [
    { value: "study",  emoji: "🎓", label: "Хэлний сургуулийн виз", desc: "Англи хэл, мэргэжлийн сургалт" },
  ],
  czech:     [
    { value: "work",   emoji: "💼", label: "Ажлын зөвшөөрөл",      desc: "Урт хугацааны ажлын виз, 3–12 сар" },
  ],
};

/* ── Document lists per country+purpose ─────────────────── */
interface DocSplit {
  ud: string[];
  user: string[];
  userNote?: string;
}
const DOCS: Record<string, Record<string, DocSplit>> = {
  usa: {
    travel: {
      ud: [
        "Дэлгэрэнгүй зөвлөгөө",
        "DS-160 анкет",
        "Ярилцлаганы цаг авах",
        "Ярилцлаганд бэлдэх",
        "Аяллын төлөвлөгөө",
        "Очих хаяг",
        "Орчуулга",
        "Өмнө нь татгалзаж байсан бол судалгаа",
        "Тухайн хүнээс шалтгаалж урилга",
        "Бусад",
      ],
      user: [
        "Гадаад паспорт",
        "Банкны хуулга эсхүл тодорхойлолт (байхгүй бол биднээс зөвлөгөө авах)",
        "Ажилтай бол ажлын газрын тодорхойлолт",
        "Бизнес эрхэлдэг бол компанийн гэрчилгээ (баталж чадахгүй бол бид зөвлөнө)",
        "Ямар нэгэн хөрөнгөтэй бол нотлох баримт",
        "НДШ төлдөг бол НДШ",
        "Гэр бүлтэй бол гэрчилгээ",
        "Хүүхэдтэй бол хүүхдийн төрсний гэрчилгээ",
      ],
      userNote: "Элчингийн цаг 5-6 сард байна — яарахгүй бол дүүрнэ. Өдөртөө визний хариу гарна.",
    },
    study: {
      ud: [
        "Виз гарах магадлал өндөр сургуулийн судалгаа",
        "Тухайн хүний цаашдын зорилгод тохирсон сургуулийн сонголт",
        "Тань тохирсон сургуулийн урилга (I-20) авах",
        "SEVIS төлөлт хийх",
        "Ярилцлаганд бэлдэх (Англи, Монгол)",
        "DS-160 анкет",
        "Ярилцлаганы цаг авах",
        "Очих хаяг",
        "Орчуулга",
        "Өмнө нь татгалзаж байсан бол судалгаа",
        "Бусад",
      ],
      user: [
        "Гадаад паспорт",
        "5×5 зураг",
        "Банкны баталгаа (бид таньд хэд хэрэгтэйг хэлнэ)",
        "Өөрөө баталгаа гаргаагүй бол гаргасан хүний орлогыг нотлох баримт",
        "Ажилтай бол ажлын газрын тодорхойлолт",
        "Бизнес эрхэлдэг бол компанийн гэрчилгээ (баталж чадахгүй бол бид зөвлөнө)",
        "Ямар нэгэн хөрөнгөтэй бол нотлох баримт",
        "НДШ төлдөг бол НДШ",
        "Гэр бүлтэй бол гэрчилгээ",
        "Хүүхэдтэй бол хүүхдийн төрсний гэрчилгээ",
      ],
      userNote: "Элчингийн цаг ойрхон байвал 1-3 сарын процесс.",
    },
  },
  australia: {
    travel: {
      ud: [
        "Дэлгэрэнгүй зөвлөгөө",
        "Онлайн анкет",
        "Аяллын төлөвлөгөө",
        "Очих хаяг",
        "Орчуулга",
        "Өмнө нь татгалзаж байсан бол судалгаа",
        "Тухайн хүнээс шалтгаалж урилга",
        "Бусад",
      ],
      user: [
        "Гадаад паспорт",
        "Цээж зураг",
        "Банкны хуулга эсхүл тодорхойлолт (байхгүй бол биднээс зөвлөгөө авах)",
        "Ажилтай бол ажлын газрын тодорхойлолт",
        "Бизнес эрхэлдэг бол компанийн гэрчилгээ (баталж чадахгүй бол бид зөвлөнө)",
        "Ямар нэгэн хөрөнгөтэй бол нотлох баримт",
        "НДШ төлдөг бол НДШ",
        "Гэр бүлтэй бол гэрчилгээ",
        "Хүүхэдтэй бол хүүхдийн төрсний гэрчилгээ",
      ],
      userNote: "Визний хариу автал 1-30 хоног.",
    },
  },
  canada: {
    travel: {
      ud: [
        "Дэлгэрэнгүй зөвлөгөө",
        "IMM олон төрлийн анкет",
        "GCKEY profile нээх",
        "Элчинд зориулсан захидал (магадлал өндөр)",
        "Материалын иж бүрэн тайлбар",
        "Онгоцны тийз захиалга",
        "Аяллын төлөвлөгөө",
        "Очих хаяг",
        "Орчуулга",
        "Өмнө нь татгалзаж байсан бол судалгаа",
        "Тухайн хүнээс шалтгаалж урилга",
        "Бусад",
      ],
      user: [
        "Гадаад паспорт",
        "Цээж зураг",
        "Банкны хуулга эсхүл тодорхойлолт (байхгүй бол биднээс зөвлөгөө авах)",
        "Ажилтай бол ажлын газрын тодорхойлолт",
        "Бизнес эрхэлдэг бол компанийн гэрчилгээ (баталж чадахгүй бол бид зөвлөнө)",
        "Ямар нэгэн хөрөнгөтэй бол нотлох баримт",
        "НДШ төлдөг бол НДШ",
        "Гэр бүлтэй бол гэрчилгээ",
        "Хүүхэдтэй бол хүүхдийн төрсний гэрчилгээ",
      ],
      userNote: "Визний хариу 1-6 долоо хоног.",
    },
  },
  schengen: {
    travel: {
      ud: [
        "Дэлгэрэнгүй зөвлөгөө",
        "Визний өргөдлийн маягт",
        "Аяллын төлөвлөгөө",
        "Очих хаяг",
        "Орчуулга",
        "Өмнө нь татгалзаж байсан бол судалгаа",
        "Тухайн хүнээс шалтгаалж урилга",
        "Бусад",
      ],
      user: [
        "Гадаад паспорт (3+ сарын хүчинтэй)",
        "Биометрик зураг (2 ш)",
        "Аяллын даатгал (€30,000+)",
        "Нислэгийн тийз (захиалга)",
        "Буудлын захиалга",
        "Банкны хуулга",
        "Ажилтай бол ажлын газрын тодорхойлолт / чөлөөний захидал",
        "Бизнес эрхэлдэг бол компанийн гэрчилгээ",
        "Ямар нэгэн хөрөнгөтэй бол нотлох баримт",
        "НДШ төлдөг бол НДШ",
        "Гэр бүлтэй бол гэрчилгээ",
        "Хүүхэдтэй бол хүүхдийн төрсний гэрчилгээ",
      ],
    },
  },
  ireland: {
    study: {
      ud: [
        "Өөрсдийн гэрээт сургуулиудад элсүүлнэ",
        "Acceptance Letter авах",
        "Дэлгэрэнгүй зөвлөгөө",
        "AVATS анкет",
        "Элчинд зориулсан захидал (магадлал өндөр)",
        "Материалын иж бүрэн тайлбар",
        "Өмнө нь татгалзаж байсан бол давж заалдах процесс",
        "Тухайн хүнээс шалтгаалж урилга",
        "Бусад",
      ],
      user: [
        "Гадаад паспорт",
        "Цээж зураг",
        "Банкны хуулга эсхүл тодорхойлолт (10,000 евро байх)",
        "Ажилтай бол ажлын газрын тодорхойлолт",
        "Бизнес эрхэлдэг бол компанийн гэрчилгээ (баталж чадахгүй бол бид зөвлөнө)",
        "Ямар нэгэн хөрөнгөтэй бол нотлох баримт",
        "НДШ төлдөг бол НДШ",
        "Гэр бүлтэй бол гэрчилгээ",
        "Хүүхэдтэй бол хүүхдийн төрсний гэрчилгээ",
      ],
      userNote: "Визний хариу автал 1-8 долоо хоног.",
    },
  },
  czech: {
    work: {
      ud: [
        "Ажлын урилга",
        "Элчингийн цаг",
        "Визний материал бүрдүүлэлт",
        "Орчуулга",
        "Бусад",
      ],
      user: [
        "Гадаад паспорт",
        "Иргэний үнэмлэх",
      ],
      userNote: "Ажлын виз тул маш цөөн материал бүрдүүлнэ — банкны баталгаа, хөрөнгийн баталгаа хэрэггүй. 3-12 сарын хугацаа шаардагдана.",
    },
  },
};

/* ── Scoring (reused from VisaChecker) ───────────────────── */
interface QuizData {
  purpose: string;
  age: string;
  employment: string;
  income: string;
  travelHistory: string;
  priorVisa: string;
  refusal: string;
  familyInCountry: string;
  familyStatus: string;
  englishLevel: string;
  czechCriminal: string;
  czechSchengenBan: string;
}

const INITIAL: QuizData = {
  purpose: "", age: "", employment: "", income: "",
  travelHistory: "", priorVisa: "", refusal: "",
  familyInCountry: "", familyStatus: "",
  englishLevel: "", czechCriminal: "", czechSchengenBan: "",
};



/* ── Reassurance messages ────────────────────────────────── */
function getReassurances(data: QuizData): string[] {
  const msgs: string[] = [];
  if (data.employment === "unemployed") {
    msgs.push("Ажилгүй байсан ч санаа зовох хэрэггүй — яг таны нөхцөл байдалтай хүмүүс манайд амжилттай виз авсан тохиолдол маш олон байдаг.");
  }
  if (data.income === "low") {
    msgs.push("Банкны данс бага байна уу? Элчин сайдын яам мөнгөний хэмжээнээс илүүтэй тогтвортой байдлыг хардаг — бид таны нөхцөлд тохирсон стратеги боловсруулна.");
  }
  if (data.refusal === "yes") {
    msgs.push("Өмнө нь татгалзал авсан ч бид тусгай стратеги боловсруулж дахин амжилттай мэдүүлсэн тохиолдол маш олон байдаг.");
  }
  if (data.familyInCountry === "yes" && (data.familyStatus === "asylum" || data.familyStatus === "deported")) {
    msgs.push("Таны нөхцөл байдал хэцүү мэт санагдаж болох ч — яг ийм нөхцөлтэй хүмүүс манайд амжилттай виз авсан байдаг. Бид хамтдаа шийдэл олно.");
  }
  if (msgs.length === 0) {
    msgs.push("Манай байгууллага таны нөхцөлтэй төстэй олон хүнд амжилттай виз гаргасан туршлагатай — бид таны хэрэгт бүрэн дэмжлэг үзүүлнэ.");
  }
  return msgs;
}

/* ── Step definitions ────────────────────────────────────── */
function getSteps(countryId: string, data: QuizData): string[] {
  const steps = ["purpose"];
  if (countryId === "czech") {
    steps.push("age", "czechCriminal", "czechSchengenBan");
  } else {
    steps.push("age", "employment", "income", "travelHistory", "priorVisa", "refusal");
    if (data.refusal !== "" || steps.length > 6) {
      // family question for non-czech
      steps.push("familyInCountry");
      if (data.familyInCountry === "yes") steps.push("familyStatus");
    }
    if (data.purpose === "study" && countryId !== "ireland") {
      steps.push("englishLevel");
    }
  }
  steps.push("result");
  return steps;
}

/* ── Option button component ─────────────────────────────── */
function OptionBtn({
  selected, onClick, emoji, label, desc,
}: {
  selected: boolean; onClick: () => void;
  emoji?: string; label: string; desc?: string;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all duration-200"
      style={{
        borderColor: selected ? "#1E6FD9" : "#e2e8f0",
        background: selected ? "rgba(30,111,217,0.06)" : "white",
      }}
    >
      {emoji && <span className="text-2xl flex-shrink-0 mt-0.5">{emoji}</span>}
      <div>
        <div className="font-semibold text-[#0A1628] text-sm" style={{ fontFamily: "var(--font-display)" }}>
          {label}
        </div>
        {desc && <div className="text-xs text-slate-500 mt-0.5">{desc}</div>}
      </div>
      {selected && <CheckCircle2 className="w-5 h-5 text-[#1E6FD9] ml-auto flex-shrink-0 mt-0.5" />}
    </button>
  );
}

//* ── Country selection list (when opened without a country) ─ */
const QUIZ_COUNTRIES = [
  { id: "usa",       flag: "🇺🇸", name: "АНУ" },
  { id: "australia", flag: "🇦🇺", name: "Австрали" },
  { id: "canada",    flag: "🇨🇦", name: "Канад" },
  { id: "czech",     flag: "🇨🇿", name: "Чех" },
  { id: "ireland",   flag: "🇮🇪", name: "Ирланд" },
  { id: "schengen",  flag: "🇪🇺", name: "Шенген" },
];
/* ── Props ───────────────────────────────────────────────── */
interface Props {
  isOpen: boolean;
  onClose: () => void;
  countryId: string;   // empty string = show country picker first
  countryFlag: string;
  countryName: string;
}
/* ── Main component ──────────────────────────────────────── */
export default function VisaQuiz({ isOpen, onClose, countryId: initialCountryId, countryFlag: initialFlag, countryName: initialName }: Props) {
  const { openConsultation } = useConsultation();
  const openOrder = useOrderModal();
  const [data, setData] = useState<QuizData>(INITIAL);
  const [stepIndex, setStepIndex] = useState(0);
  const [showConsultForm, setShowConsultForm] = useState(false);
  const [consultPhone, setConsultPhone] = useState("");
  const [consultEmail, setConsultEmail] = useState("");
  const [consultSubmitted, setConsultSubmitted] = useState(false);
  const submitConsultation = trpc.callbacks.submitConsultation.useMutation();
  // Country can be selected inside the quiz when opened without one
  const [selectedCountry, setSelectedCountry] = useState<{ id: string; flag: string; name: string } | null>(
    initialCountryId ? { id: initialCountryId, flag: initialFlag, name: initialName } : null
  );
  const countryId   = selectedCountry?.id   ?? "";
  const countryFlag = selectedCountry?.flag ?? "";
  const countryName = selectedCountry?.name ?? "";
  const showCountryPicker = !selectedCountry;
  const steps = useMemo(() => getSteps(countryId, data), [countryId, data]);
  const currentStep = steps[stepIndex];
  const isResult = currentStep === "result";
  const tier = calcTier();
  const visaKey = COUNTRY_VISA_KEY[countryId]?.[data.purpose] ?? COUNTRY_VISA_KEY[countryId]?.["travel"] ?? "";
  const visa = VISA_PRICING.find(v => v.visaKey === visaKey);
  const price = visa ? getCurrentPrice(visa, tier) : "";
  const docSplit: DocSplit = DOCS[countryId]?.[data.purpose] ?? DOCS[countryId]?.["travel"] ?? { ud: [], user: [] };
  const reassurances = useMemo(() => getReassurances(data), [data]);
  const purposes = COUNTRY_PURPOSES[countryId] ?? [];

  function set(field: keyof QuizData, value: string) {
    setData(prev => ({ ...prev, [field]: value }));
  }

  function canAdvance(): boolean {
    switch (currentStep) {
      case "purpose": return data.purpose !== "";
      case "age": return data.age !== "";
      case "employment": return data.employment !== "";
      case "income": return data.income !== "";
      case "travelHistory": return data.travelHistory !== "";
      case "priorVisa": return data.priorVisa !== "";
      case "refusal": return data.refusal !== "";
      case "familyInCountry": return data.familyInCountry !== "";
      case "familyStatus": return data.familyStatus !== "";
      case "englishLevel": return data.englishLevel !== "";
      case "czechCriminal": return data.czechCriminal !== "";
      case "czechSchengenBan": return data.czechSchengenBan !== "";
      default: return true;
    }
  }

  function advance() {
    // Recompute steps with latest data to handle conditional steps
    const freshSteps = getSteps(countryId, data);
    if (stepIndex < freshSteps.length - 1) {
      setStepIndex(i => i + 1);
    }
  }

  function back() {
    if (stepIndex > 0) setStepIndex(i => i - 1);
  }

  function reset() {
    setData(INITIAL);
    setStepIndex(0);
    setShowConsultForm(false);
    setConsultPhone("");
    setConsultEmail("");
    setConsultSubmitted(false);
    // If opened without a country, go back to country picker
    if (!initialCountryId) setSelectedCountry(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  const totalSteps = steps.filter(s => s !== "result").length;
  const progress = isResult ? 100 : Math.round((stepIndex / totalSteps) * 100);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="quiz-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: "rgba(10,22,40,0.7)", backdropFilter: "blur(4px)" }}
        onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
      >
        <motion.div
          key="quiz-modal"
          initial={{ opacity: 0, y: 32, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.97 }}
          transition={{ duration: 0.3 }}
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-[#0A1628] rounded-t-2xl px-5 py-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2.5">
                {showCountryPicker ? (
                  <span className="text-2xl">🌍</span>
                ) : (
                  <span className="text-2xl">{countryFlag}</span>
                )}
                <div>
                  <div className="text-white font-bold text-sm" style={{ fontFamily: "var(--font-display)" }}>
                    {showCountryPicker ? "Улс сонгоно уу" : `${countryName} — Визийн шалгуур`}
                  </div>
                  <div className="text-white/50 text-xs">
                    {showCountryPicker ? "Таны зорилгоч улсаа сонгоно уу" : isResult ? "Таны үр дүн бэлэн боллоо" : `${stepIndex + 1} / ${totalSteps} асуулт`}
                  </div>
                </div>
              </div>
              <button onClick={handleClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
            {/* Progress bar - hidden on country picker */}
            {!showCountryPicker && (
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-[#3b82f6]"
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>
            )}
          </div>

          {/* Body */}
          <div className="p-5">
            <AnimatePresence mode="wait">
              {/* ── COUNTRY PICKER ─────────────────────────────── */}
              {showCountryPicker && (
                <motion.div key="country-picker" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-1" style={{ fontFamily: "var(--font-display)" }}>
                    Ямар улс руу явах вэ?
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">Виз авах улсаа сонгоно уу</p>
                  <div className="grid grid-cols-2 gap-2.5">
                    {QUIZ_COUNTRIES.map(c => (
                      <button
                        key={c.id}
                        onClick={() => setSelectedCountry(c)}
                        className="flex items-center gap-2.5 p-3.5 rounded-xl border-2 border-slate-200 hover:border-[#1E6FD9] hover:bg-[#1E6FD9]/5 transition-all text-left"
                      >
                        <span className="text-2xl flex-shrink-0">{c.flag}</span>
                        <span className="font-semibold text-[#0A1628] text-sm" style={{ fontFamily: "var(--font-display)" }}>{c.name}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
              {/* ── PURPOSE ─────────────────────────────────────── */}
              {!showCountryPicker && currentStep === "purpose" && (
                <motion.div key="purpose" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-1" style={{ fontFamily: "var(--font-display)" }}>
                    Ямар зорилгоор явах вэ?
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">Визийн төрлөө сонгоно уу</p>
                  <div className="space-y-2.5">
                    {purposes.map(p => (
                      <OptionBtn key={p.value} selected={data.purpose === p.value} onClick={() => { set("purpose", p.value); }}
                        emoji={p.emoji} label={p.label} desc={p.desc} />
                    ))}
                  </div>
                </motion.div>
              )}

              {/* ── AGE ─────────────────────────────────── */}
              {!showCountryPicker && currentStep === "age" && (
                <motion.div key="age" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-1" style={{ fontFamily: "var(--font-display)" }}>Таны нас хэд вэ?</h3>
                  <p className="text-sm text-slate-500 mb-4">Насны бүлгээ сонгоно уу</p>
                  <div className="space-y-2.5">
                    {[
                      { value: "18-24", label: "18–24 нас" },
                      { value: "25-40", label: "25–40 нас" },
                      { value: "41-55", label: "41–55 нас" },
                      { value: "56+",   label: "56+ нас" },
                    ].map(o => (
                      <OptionBtn key={o.value} selected={data.age === o.value} onClick={() => set("age", o.value)} label={o.label} />
                    ))}
                  </div>
                </motion.div>
              )}

              {/* ── EMPLOYMENT ──────────────────────────── */}
              {!showCountryPicker && currentStep === "employment" && (
                <motion.div key="employment" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-1" style={{ fontFamily: "var(--font-display)" }}>Ажил эрхлэлтийн байдал?</h3>
                  <p className="text-sm text-slate-500 mb-4">Одоогийн нөхцөл байдлаа сонгоно уу</p>
                  <div className="space-y-2.5">
                    {[
                      { value: "employed",   emoji: "💼", label: "Ажилтай", desc: "Байгууллага, компанид ажилладаг" },
                      { value: "business",   emoji: "🏢", label: "Бизнес эрхлэгч", desc: "Өөрийн бизнестэй" },
                      { value: "student",    emoji: "🎓", label: "Оюутан", desc: "Их сургууль, коллежид суралцдаг" },
                      { value: "retired",    emoji: "🌿", label: "Тэтгэвэрт гарсан", desc: "Тэтгэвэр, тэтгэмжтэй" },
                      { value: "unemployed", emoji: "🏠", label: "Ажилгүй", desc: "Одоогоор ажилгүй байна" },
                    ].map(o => (
                      <OptionBtn key={o.value} selected={data.employment === o.value} onClick={() => set("employment", o.value)}
                        emoji={o.emoji} label={o.label} desc={o.desc} />
                    ))}
                  </div>
                  {data.employment === "unemployed" && (
                    <div className="mt-3 p-3.5 rounded-xl bg-blue-50 border border-blue-200 text-xs text-blue-800">
                      💪 Ажилгүй байсан ч санаа зовох хэрэггүй — яг таны нөхцөл байдалтай хүмүүс манайд амжилттай виз авсан тохиолдол маш олон байдаг.
                    </div>
                  )}
                </motion.div>
              )}

              {/* ── INCOME ──────────────────────────────── */}
              {!showCountryPicker && currentStep === "income" && (
                <motion.div key="income" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-1" style={{ fontFamily: "var(--font-display)" }}>Сарын орлого / банкны данс?</h3>
                  <p className="text-sm text-slate-500 mb-4">Ойролцоо хэмжээгээ сонгоно уу</p>
                  <div className="space-y-2.5">
                    {[
                      { value: "high",   emoji: "💰", label: "3 сая₮-с дээш / сар", desc: "Эсвэл банкны данс 10 сая₮+" },
                      { value: "medium", emoji: "💵", label: "1–3 сая₮ / сар",       desc: "Эсвэл банкны данс 3–10 сая₮" },
                      { value: "low",    emoji: "💳", label: "1 сая₮-с доош / сар",  desc: "Эсвэл банкны данс 3 сая₮-с доош" },
                    ].map(o => (
                      <OptionBtn key={o.value} selected={data.income === o.value} onClick={() => set("income", o.value)}
                        emoji={o.emoji} label={o.label} desc={o.desc} />
                    ))}
                  </div>
                  {data.income === "low" && (
                    <div className="mt-3 p-3.5 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800">
                      💡 Банкны данс бага байна уу? Элчин сайдын яам мөнгөний хэмжээнээс илүүтэй <strong>тогтвортой байдлыг</strong> хардаг. Гэнэт их мөнгө оруулах нь эсрэгээрээ сэжиг төрүүлдэг — бид таны нөхцөлд тохирсон стратеги боловсруулна.
                    </div>
                  )}
                </motion.div>
              )}

              {/* ── TRAVEL HISTORY ──────────────────────── */}
              {!showCountryPicker && currentStep === "travelHistory" && (
                <motion.div key="travel" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-1" style={{ fontFamily: "var(--font-display)" }}>Сүүлийн 5 жилд гадаадад явсан уу?</h3>
                  <p className="text-sm text-slate-500 mb-4">Аяллын түүхээ сонгоно уу</p>
                  <div className="space-y-2.5">
                    {[
                      { value: "many", emoji: "🌍", label: "Тийм, олон улсад явсан", desc: "3+ улс, олон удаа" },
                      { value: "some", emoji: "✈️", label: "Тийм, хэдэн удаа явсан", desc: "1–2 улс" },
                      { value: "none", emoji: "🏠", label: "Үгүй, явж байгаагүй",    desc: "Анх удаа гадаадад явна" },
                    ].map(o => (
                      <OptionBtn key={o.value} selected={data.travelHistory === o.value} onClick={() => set("travelHistory", o.value)}
                        emoji={o.emoji} label={o.label} desc={o.desc} />
                    ))}
                  </div>
                </motion.div>
              )}

              {/* ── PRIOR VISA ──────────────────────────── */}
              {!showCountryPicker && currentStep === "priorVisa" && (
                <motion.div key="priorVisa" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-1" style={{ fontFamily: "var(--font-display)" }}>Өмнө нь гадаадын виз авсан уу?</h3>
                  <p className="text-sm text-slate-500 mb-4">Аль нэг улсын виз авсан бол "Тийм" гэнэ</p>
                  <div className="space-y-2.5">
                    <OptionBtn selected={data.priorVisa === "yes"} onClick={() => set("priorVisa", "yes")} emoji="✅" label="Тийм, виз авсан байсан" desc="Шенген, АНУ, Австрали гэх мэт" />
                    <OptionBtn selected={data.priorVisa === "no"} onClick={() => set("priorVisa", "no")} emoji="❌" label="Үгүй, авч байгаагүй" />
                  </div>
                </motion.div>
              )}

              {/* ── REFUSAL ─────────────────────────────── */}
              {!showCountryPicker && currentStep === "refusal" && (
                <motion.div key="refusal" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-1" style={{ fontFamily: "var(--font-display)" }}>
                    {countryName}-ын визэнд татгалзуулсан уу?
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">Өмнө нь татгалзал авсан эсэхийг сонгоно уу</p>
                  <div className="space-y-2.5">
                    <OptionBtn selected={data.refusal === "no"} onClick={() => set("refusal", "no")} emoji="✅" label="Үгүй, татгалзуулж байгаагүй" />
                    <OptionBtn selected={data.refusal === "yes"} onClick={() => set("refusal", "yes")} emoji="⚠️" label="Тийм, татгалзуулсан байсан" />
                  </div>
                  {data.refusal === "yes" && (
                    <div className="mt-3 p-3.5 rounded-xl bg-blue-50 border border-blue-200 text-xs text-blue-800">
                      💪 Өмнөх татгалзал байсан ч бид тусгай стратеги боловсруулж дахин амжилттай мэдүүлсэн тохиолдол маш олон байдаг. Санаа зовох хэрэггүй.
                    </div>
                  )}
                </motion.div>
              )}

              {/* ── FAMILY IN COUNTRY ───────────────────── */}
              {!showCountryPicker && currentStep === "familyInCountry" && (
                <motion.div key="family" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-1" style={{ fontFamily: "var(--font-display)" }}>
                    {countryName}-д ойр дотны хүн байгаа уу?
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">Биологийн эцэг эх, ах дүү, хүүхэд</p>
                  <div className="space-y-2.5">
                    <OptionBtn selected={data.familyInCountry === "yes"} onClick={() => set("familyInCountry", "yes")} emoji="👨‍👩‍👧" label="Тийм, байгаа" />
                    <OptionBtn selected={data.familyInCountry === "no"} onClick={() => set("familyInCountry", "no")} emoji="❌" label="Үгүй, байхгүй" />
                  </div>
                </motion.div>
              )}

              {/* ── FAMILY STATUS ───────────────────────── */}
              {!showCountryPicker && currentStep === "familyStatus" && (
                <motion.div key="familyStatus" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-1" style={{ fontFamily: "var(--font-display)" }}>
                    Тэр хүний визийн статус?
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">Одоогийн байдлаа сонгоно уу</p>
                  <div className="space-y-2.5">
                    {[
                      { value: "citizen",   emoji: "🏅", label: "Иргэн", desc: "Тухайн улсын иргэн" },
                      { value: "greencard", emoji: "🟢", label: "Байнгын оршин суугч", desc: "Green card, PR" },
                      { value: "study",     emoji: "🎓", label: "Оюутны / Ажлын виз", desc: "Хүчинтэй виз, зөвшөөрөлтэй" },
                      { value: "asylum",    emoji: "⚠️", label: "Дүрвэгч / Хоргодогч", desc: "Asylum, дүрвэгчийн статус" },
                      { value: "deported",  emoji: "🚫", label: "Хууль бусаар оршин суугч", desc: "Виз дуусгавар болсон, хөөгдсөн" },
                    ].map(o => (
                      <OptionBtn key={o.value} selected={data.familyStatus === o.value} onClick={() => set("familyStatus", o.value)}
                        emoji={o.emoji} label={o.label} desc={o.desc} />
                    ))}
                  </div>
                </motion.div>
              )}

              {/* ── ENGLISH LEVEL ───────────────────────── */}
              {!showCountryPicker && currentStep === "englishLevel" && (
                <motion.div key="english" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-1" style={{ fontFamily: "var(--font-display)" }}>Англи хэлний түвшин?</h3>
                  <p className="text-sm text-slate-500 mb-4">Сургалтын визэнд чухал шаардлага</p>
                  <div className="space-y-2.5">
                    {[
                      { value: "advanced",     emoji: "🌟", label: "Дэвшилтэт (C1/C2)", desc: "IELTS 7+, TOEFL 100+" },
                      { value: "upper",        emoji: "✅", label: "Дунд дээш (B2)",     desc: "IELTS 5.5–6.5" },
                      { value: "intermediate", emoji: "📚", label: "Дунд (B1)",           desc: "IELTS 4.5–5.5" },
                      { value: "basic",        emoji: "📖", label: "Суурь (A1/A2)",       desc: "IELTS 4-с доош" },
                    ].map(o => (
                      <OptionBtn key={o.value} selected={data.englishLevel === o.value} onClick={() => set("englishLevel", o.value)}
                        emoji={o.emoji} label={o.label} desc={o.desc} />
                    ))}
                  </div>
                </motion.div>
              )}

              {/* ── CZECH CRIMINAL ──────────────────────── */}
              {!showCountryPicker && currentStep === "czechCriminal" && (
                <motion.div key="czechCriminal" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-1" style={{ fontFamily: "var(--font-display)" }}>Эрүүгийн хэрэгт холбогдож байсан уу?</h3>
                  <p className="text-sm text-slate-500 mb-4">Монгол болон гадаадад</p>
                  <div className="space-y-2.5">
                    <OptionBtn selected={data.czechCriminal === "no"} onClick={() => set("czechCriminal", "no")} emoji="✅" label="Үгүй, байгаагүй" />
                    <OptionBtn selected={data.czechCriminal === "yes"} onClick={() => set("czechCriminal", "yes")} emoji="⚠️" label="Тийм, байсан" />
                  </div>
                </motion.div>
              )}

              {/* ── CZECH SCHENGEN BAN ──────────────────── */}
              {!showCountryPicker && currentStep === "czechSchengenBan" && (
                <motion.div key="czechBan" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-1" style={{ fontFamily: "var(--font-display)" }}>Шенгений бүсэд хориглогдсон уу?</h3>
                  <p className="text-sm text-slate-500 mb-4">Шенгений орнуудаас хориглогдсон эсэх</p>
                  <div className="space-y-2.5">
                    <OptionBtn selected={data.czechSchengenBan === "no"} onClick={() => set("czechSchengenBan", "no")} emoji="✅" label="Үгүй, хориглогдоогүй" />
                    <OptionBtn selected={data.czechSchengenBan === "yes"} onClick={() => set("czechSchengenBan", "yes")} emoji="🚫" label="Тийм, хориглогдсон" />
                  </div>
                </motion.div>
              )}

              {/* ── RESULT ──────────────────────────────── */}
              {!showCountryPicker && isResult && (
                <motion.div key="result" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
                  {/* Eligibility message */}
                  <div className="mb-5">
                    <div
                      className="rounded-2xl p-5 text-center"
                      style={{ background: "linear-gradient(135deg, rgba(30,111,217,0.07) 0%, rgba(59,130,246,0.10) 100%)", border: "2px solid rgba(30,111,217,0.18)" }}
                    >
                      <div className="text-3xl mb-2">🎉</div>
                      <h3 className="text-lg font-black text-[#0A1628] mb-2" style={{ fontFamily: "var(--font-display)" }}>
                        Баяр хүргье!
                      </h3>
                      <p className="text-sm text-slate-700 leading-relaxed mb-1" style={{ fontFamily: "var(--font-body)" }}>
                        Таны нөхцөл байдлыг үнэлэхэд <span className="font-bold text-[#1E6FD9]">виз авах боломжтой байна.</span>
                      </p>
                      <p className="text-xs text-slate-500 leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
                        Виз авах магадлал нь эртнээс бэлдэж эхлэхээс шалтгаалдаг тул визний бэлтгэлээ аль болох эрт хангаарай.
                      </p>
                    </div>
                  </div>

                  {/* Reassurance */}
                  <div className="mb-4 space-y-2">
                    {reassurances.map((msg, i) => (
                      <div key={i} className="flex items-start gap-2.5 p-3 rounded-xl bg-blue-50 border border-blue-100">
                        <span className="text-base flex-shrink-0">💪</span>
                        <p className="text-xs text-blue-800 leading-relaxed">{msg}</p>
                      </div>
                    ))}
                  </div>

                  {/* Document list */}
                  {(docSplit.ud.length > 0 || docSplit.user.length > 0) && (
                    <div className="mb-4">
                      <h4 className="font-bold text-[#0A1628] text-sm mb-2.5" style={{ fontFamily: "var(--font-display)" }}>
                        📋 Баримт бичгийн жагсаалт
                      </h4>
                      <div className="flex flex-col gap-3">
                        {/* United Dream prepares */}
                        {docSplit.ud.length > 0 && (
                          <div className="bg-blue-50 rounded-xl border border-blue-100 p-3.5">
                            <div className="flex items-center gap-1.5 mb-2">
                              <span className="text-sm">🏢</span>
                              <span className="text-xs font-bold text-blue-700" style={{ fontFamily: "var(--font-display)" }}>United Dream хийж өгнө</span>
                            </div>
                            <ul className="space-y-1.5">
                              {docSplit.ud.map((doc: string, i: number) => (
                                <li key={i} className="flex items-start gap-1.5 text-xs text-blue-800">
                                  <CheckCircle2 className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
                                  {doc}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {/* User prepares */}
                        {docSplit.user.length > 0 && (
                          <div className="bg-slate-50 rounded-xl border border-slate-100 p-3.5">
                            <div className="flex items-center gap-1.5 mb-2">
                              <span className="text-sm">👤</span>
                              <span className="text-xs font-bold text-slate-700" style={{ fontFamily: "var(--font-display)" }}>Та өөрөө бүрдүүлнэ</span>
                            </div>
                            <ul className="space-y-1.5">
                              {docSplit.user.map((doc: string, i: number) => (
                                <li key={i} className="flex items-start gap-1.5 text-xs text-slate-700">
                                  <CheckCircle2 className="w-3.5 h-3.5 text-[#1E6FD9] flex-shrink-0 mt-0.5" />
                                  {doc}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {/* User note */}
                        {docSplit.userNote && (
                          <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-100">
                            <span className="text-sm flex-shrink-0">ℹ️</span>
                            <p className="text-xs text-amber-800 leading-relaxed">{docSplit.userNote}</p>
                          </div>
                        )}
                      </div>
                      {/* School link for study visa — USA or Ireland */}
                      {data.purpose === "study" && (countryId === "usa" || countryId === "ireland") && (
                        <a
                          href={countryId === "usa" ? "/country/usa#schools" : "/country/ireland#schools"}
                          className="mt-3 flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold transition-all hover:-translate-y-0.5"
                          style={{
                            background: "linear-gradient(135deg, rgba(16,185,129,0.10) 0%, rgba(52,211,153,0.06) 100%)",
                            border: "1px solid rgba(52,211,153,0.35)",
                            color: "#059669",
                            fontFamily: "var(--font-display)",
                          }}
                          onClick={() => handleClose()}
                        >
                          <span>🏫 {countryId === "usa" ? "АНУ-ын сургуулиудтай танилцах →" : "Ирландын сургуулиудтай танилцах →"}</span>
                          <ArrowRight className="w-4 h-4" />
                        </a>
                      )}
                      {/* Cost calculator link — USA study only */}
                      {data.purpose === "study" && countryId === "usa" && (
                        <a
                          href="/us-cost-calculator"
                          className="mt-2 flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold transition-all hover:-translate-y-0.5"
                          style={{
                            background: "linear-gradient(135deg, rgba(30,111,217,0.10) 0%, rgba(59,130,246,0.06) 100%)",
                            border: "1px solid rgba(30,111,217,0.30)",
                            color: "#1E6FD9",
                            fontFamily: "var(--font-display)",
                          }}
                          onClick={() => handleClose()}
                        >
                          <span>🎓 Нийт зардлаа тооцоолох →</span>
                          <ArrowRight className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  )}

                  {/* Cost */}
                  {visa && (
                    <div className="mb-5 p-4 rounded-xl bg-gradient-to-r from-[#0A1628] to-[#1a2d4a] text-white">
                      <div className="text-xs text-white/60 mb-1">Үйлчилгээний хөлс</div>
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className="text-2xl font-black" style={{ fontFamily: "var(--font-display)" }}>{price}</span>
                        {tier === "sale" && visa.normalPrice !== visa.salePrice && (
                          <span className="text-sm text-white/40 line-through">{visa.normalPrice}</span>
                        )}
                      </div>
                      {visa.visaFeeNote && (
                        <div className="text-xs text-white/60">
                          + {visa.visaFeeNote}: {visa.visaFeeAmount}
                        </div>
                      )}
                      {tier === "sale" && (
                        <div className="mt-2">
                          <div className="text-xs text-amber-300 font-semibold">🔥 Хямдралтай үнэ — 3 сарын эцэс хүртэл</div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* CTA buttons */}
                  <div className="flex flex-col gap-2.5">
                    <button
                      onClick={() => { handleClose(); setTimeout(() => openOrder(visaKey), 300); }}
                      className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-white font-bold text-sm transition-all hover:opacity-90 active:scale-95"
                      style={{ background: "linear-gradient(135deg, #1E6FD9, #3b82f6)", boxShadow: "0 4px 14px rgba(30,111,217,0.35)", fontFamily: "var(--font-display)" }}
                    >
                      Виз мэдүүлэх
                      <ArrowRight className="w-4 h-4" />
                    </button>

                    {/* Free consultation button */}
                    <a
                      href="#" onClick={(e) => { e.preventDefault(); openConsultation(); }}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-3 rounded-xl text-[#1E6FD9] text-sm font-semibold border border-[#1E6FD9]/30 bg-blue-50/60 hover:bg-blue-50 transition-all flex items-center justify-center"
                      style={{ fontFamily: "var(--font-display)" }}
                    >
                      💬 Итгэлгүй байна уу? Үнэгүй зөвлөгөө авах
                    </a>

                    {/* Consultation phone + email form */}
                    {showConsultForm && !consultSubmitted && (
                      <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                        <p className="text-xs text-blue-800 font-semibold mb-2.5" style={{ fontFamily: "var(--font-display)" }}>
                          📞 Холбоо барилах мэдээлээ үлдээгээрэй — бид 24 цагт холбогдоно. Имэйл хаягт визийн материалын жагсаалт илгээнэ.
                        </p>
                        <div className="flex flex-col gap-2">
                          <input
                            type="tel"
                            value={consultPhone}
                            onChange={(e) => setConsultPhone(e.target.value)}
                            placeholder="Утасны дугаар (+976 9999 9999)"
                            className="w-full px-3 py-2.5 rounded-lg border border-blue-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1E6FD9]/30"
                          />
                          <input
                            type="email"
                            value={consultEmail}
                            onChange={(e) => setConsultEmail(e.target.value)}
                            placeholder="Имэйл хаяг (заавал) *"
                            className="w-full px-3 py-2.5 rounded-lg border border-blue-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1E6FD9]/30"
                          />
                          <button
                            onClick={async () => {
                              if (!consultPhone.trim()) return;
                              // Save to DB via tRPC
                              await submitConsultation.mutateAsync({
                                phone: consultPhone.trim(),
                                email: consultEmail.trim(),
                                country: countryId,
                                countryName: countryName,
                                purpose: data.purpose,
                              });
                              // Also send to Google Sheets + email via Apps Script (client-side to bypass CORS restrictions)
                              try {
                                const APPS_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbwjFTTBkSA57Vf290qZ7pnzptKzF0eTG6vEYOfmIaEnBFEP1Q_5-mQRIGAWKj6quSo2/exec";
                                await fetch(APPS_SCRIPT_URL, {
                                  method: "POST",
                                  mode: "no-cors",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({
                                    phone: consultPhone.trim(),
                                    email: consultEmail.trim() || "",
                                    country: countryId,
                                    purpose: data.purpose,
                                  }),
                                });
                              } catch (e) {
                                // Silently ignore Apps Script errors — data is already saved in DB
                                console.warn("Apps Script webhook failed:", e);
                              }
                              setConsultSubmitted(true);
                              setShowConsultForm(false);
                            }}
                            disabled={!consultPhone.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(consultEmail.trim()) || submitConsultation.isPending}
                            className="w-full py-2.5 rounded-lg bg-[#1E6FD9] text-white text-sm font-bold disabled:opacity-50 transition-all hover:bg-blue-600"
                            style={{ fontFamily: "var(--font-display)" }}
                          >
                            {submitConsultation.isPending ? "..." : "Илгээх"}
                          </button>
                        </div>
                        <button onClick={() => setShowConsultForm(false)} className="mt-2 text-xs text-slate-400 hover:text-slate-600">
                          Болих
                        </button>
                      </div>
                    )}

                    {/* Success message */}
                    {consultSubmitted && (
                      <div className="rounded-xl border border-green-100 bg-green-50 p-3.5 text-center">
                        <p className="text-sm font-semibold text-green-700" style={{ fontFamily: "var(--font-display)" }}>✅ Амжилттай илгээгдлээ!</p>
                        <p className="text-xs text-green-600 mt-0.5">Бид удахгүй холбогдоно.</p>
                      </div>
                    )}

                    <button
                      onClick={reset}
                      className="w-full py-2.5 rounded-xl text-slate-500 text-sm font-medium border border-slate-200 hover:bg-slate-50 transition-all"
                    >
                      Дахин шалгах
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer nav (not on result) */}
          {!showCountryPicker && !isResult && (
            <div className="sticky bottom-0 bg-white border-t border-slate-100 px-5 py-4 rounded-b-2xl flex gap-3">
              {stepIndex > 0 && (
                <button onClick={back}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-all">
                  <ChevronLeft className="w-4 h-4" /> Буцах
                </button>
              )}
              <button
                onClick={advance}
                disabled={!canAdvance()}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-white text-sm font-bold transition-all disabled:opacity-40"
                style={{ background: canAdvance() ? "linear-gradient(135deg, #1E6FD9, #3b82f6)" : "#94a3b8", fontFamily: "var(--font-display)" }}
              >
                {stepIndex === totalSteps - 1 ? "Үр дүн харах" : "Үргэлжлүүлэх"}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
